import type { RootState } from '..';
import type { ISearchResult } from './types';
import type { IFolder } from '../folder/types';
import type { IContent } from '../content/types';

export const selectCurrentFolderId = (state: RootState) =>
  state.contentBrowser.currentFolderId || 'root';

export const isSelectedFolder = (folderId: string) => (state: RootState) =>
  state.contentBrowser.selectedFolders.includes(folderId);

export const isSelectedContent = (contentId: string) => (state: RootState) =>
  state.contentBrowser.selectedContent.includes(contentId);

export const selectSearchLoading = (state: RootState) => state.contentBrowser.loading;

export const selectSearchError = (state: RootState) => state.contentBrowser.error;

export const selectPageInfo = (state: RootState) => state.contentBrowser.pageInfo;

export const selectBreadcrumbs = (state: RootState) => state.contentBrowser.breadcrumbs;

export const selectSearchQuery = (state: RootState) => state.contentBrowser.searchQuery;

export const selectViewMode = (state: RootState) => state.contentBrowser.viewMode;

export const selectSearchResults = (state: RootState) => state.contentBrowser.searchResults;

// Derived selectors for transforming search results into display format
export const get_folders = (state: RootState): IFolder[] => {
  const workspaceId = state.workspace.currentWorkspace?.id;
  const folderId = state.contentBrowser.currentFolderId;
  const viewMode = state.contentBrowser.viewMode;

  if (viewMode === 'folder') {
    return state.folder.folder.filter(
      (folder) => folder.workspace_id === workspaceId && folder.parent_id === folderId
    );
  } else {
    const searchResults = state.contentBrowser.searchResults;

    return searchResults
      .filter((result) => result.index === 'folder')
      .map((result) => ({
        id: result.id,
        name: result.source.name,
        parent_id: result.source.folder_id || '',
        workspace_id: result.source.workspace_id || workspaceId || 'private',
        color: result.source.color,
      }));
  }
};

export const get_content = (state: RootState): IContent[] => {
  const searchResults = state.contentBrowser.searchResults;
  const workspaceId = state.workspace.currentWorkspace?.id || 'private';
  const folderId = state.contentBrowser.currentFolderId;

  if (state.contentBrowser.viewMode === 'folder') {
    return state.content.content.filter(
      (content: IContent) => content.workspace_id === workspaceId && content.folder_id === folderId
    );
  } else {
    return searchResults
      .filter((result: ISearchResult) => result.index === 'content')
      .map((result: ISearchResult) => ({
        id: result.id,
        name: result.source.name,
        content_type: result.source.content_type,
        folder_id: result.source.folder_id || '',
        workspace_id: result.source.workspace_id || workspaceId,
        library: result.source.library || '',
      }));
  }
};

// Selector to get all visible folder IDs in current view
export const selectVisibleFolderIds = (state: RootState): string[] => {
  const folders = get_folders(state);
  return folders.map((folder) => folder.id);
};

// Selector to get all visible content IDs in current view
export const selectVisibleContentIds = (state: RootState): string[] => {
  const content = get_content(state);
  return content.map((item) => item.id);
};

// Selector to check if all items are selected
export const selectAllItemsSelected = (state: RootState): boolean => {
  const visibleFolderIds = selectVisibleFolderIds(state);
  const visibleContentIds = selectVisibleContentIds(state);
  const { selectedFolders, selectedContent } = state.contentBrowser;

  const totalVisible = visibleFolderIds.length + visibleContentIds.length;

  // If there are no items, return false
  if (totalVisible === 0) {
    return false;
  }

  // Check if all visible folders are selected
  const allFoldersSelected = visibleFolderIds.every((id) => selectedFolders.includes(id));

  // Check if all visible content are selected
  const allContentSelected = visibleContentIds.every((id) => selectedContent.includes(id));

  return allFoldersSelected && allContentSelected;
};

// Selector to check if some (but not all) items are selected
export const selectSomeItemsSelected = (state: RootState): boolean => {
  const visibleFolderIds = selectVisibleFolderIds(state);
  const visibleContentIds = selectVisibleContentIds(state);
  const { selectedFolders, selectedContent } = state.contentBrowser;

  const totalVisible = visibleFolderIds.length + visibleContentIds.length;

  // If there are no items, return false
  if (totalVisible === 0) {
    return false;
  }

  // Count how many visible items are selected
  const selectedVisibleFolders = visibleFolderIds.filter((id) =>
    selectedFolders.includes(id)
  ).length;
  const selectedVisibleContent = visibleContentIds.filter((id) =>
    selectedContent.includes(id)
  ).length;

  const totalSelected = selectedVisibleFolders + selectedVisibleContent;

  // Return true if some but not all items are selected
  return totalSelected > 0 && totalSelected < totalVisible;
};

// Selector to get selected folder IDs
export const selectSelectedFolderIds = (state: RootState): string[] =>
  state.contentBrowser.selectedFolders;

// Selector to get selected content IDs
export const selectSelectedContentIds = (state: RootState): string[] =>
  state.contentBrowser.selectedContent;

// Selector to get total number of selected items
export const selectNumSelectedItems = (state: RootState): number =>
  state.contentBrowser.selectedFolders.length + state.contentBrowser.selectedContent.length;

// Selector to check if clipboard cut is active
export const selectClipboardCutActive = (state: RootState): boolean =>
  state.contentBrowser.clipboardAction === 'cut';

// Selector to get clipboard folder IDs
export const selectClipboardFolderIds = (state: RootState): string[] =>
  state.contentBrowser.clipboardFolders;

// Selector to get clipboard content IDs
export const selectClipboardContentIds = (state: RootState): string[] =>
  state.contentBrowser.clipboardContent;

// Selector to get total number of clipboard items
export const selectNumClipboardItems = (state: RootState): number =>
  state.contentBrowser.clipboardFolders.length + state.contentBrowser.clipboardContent.length;

// Selector to get clipboard folders with their full objects
export const selectClipboardFolders = (state: RootState): IFolder[] => {
  const clipboardFolderIds = state.contentBrowser.clipboardFolders;
  return state.folder.folder.filter((folder) => clipboardFolderIds.includes(folder.id));
};

// Selector to get clipboard content with their full objects
export const selectClipboardContent = (state: RootState): IContent[] => {
  const clipboardContentIds = state.contentBrowser.clipboardContent;
  return state.content.content.filter((content) => clipboardContentIds.includes(content.id));
};

// ----------------------------------------------------------------------
// Upload Selectors
// ----------------------------------------------------------------------

// Selector to get all uploads as an array
export const selectUploads = (state: RootState) => Object.values(state.contentBrowser.uploads);

// Selector to get uploads for a specific folder
export const selectUploadsForFolder =
  (workspaceId: string, folderId: string) => (state: RootState) =>
    Object.values(state.contentBrowser.uploads).filter(
      (upload) => upload.workspaceId === workspaceId && upload.folderId === folderId
    );

// Selector to get active uploads (not completed or failed)
export const selectActiveUploads = (state: RootState) =>
  Object.values(state.contentBrowser.uploads).filter(
    (upload) =>
      upload.uploadProgress.stage !== 'completed' && upload.uploadProgress.stage !== 'failed'
  );

// Selector to get completed uploads
export const selectCompletedUploads = (state: RootState) =>
  Object.values(state.contentBrowser.uploads).filter(
    (upload) => upload.uploadProgress.stage === 'completed'
  );

// Selector to get failed uploads
export const selectFailedUploads = (state: RootState) =>
  Object.values(state.contentBrowser.uploads).filter(
    (upload) => upload.uploadProgress.stage === 'failed'
  );

// Selector to check if any uploads are in progress
export const selectIsUploading = (state: RootState) =>
  Object.values(state.contentBrowser.uploads).some(
    (upload) =>
      upload.uploadProgress.stage !== 'completed' && upload.uploadProgress.stage !== 'failed'
  );

// Selector to get a specific upload by ID
export const selectUploadById = (uploadId: string) => (state: RootState) =>
  state.contentBrowser.uploads[uploadId];
